import axios from "axios";
import { getToken, clearToken } from "./auth";

// Prefer the runtime origin so the same build works on both preview and
// production domains.  The build-time `REACT_APP_BACKEND_URL` is baked in
// when CRA compiles, so a build deployed at propertymotionai.com would
// otherwise keep calling the preview URL forever.  Falling back to the
// env var only matters for SSR/test contexts where `window` is undefined.
const BACKEND_URL =
  (typeof window !== "undefined" && window.location?.origin) ||
  process.env.REACT_APP_BACKEND_URL ||
  "";

export const API = `${BACKEND_URL}/api`;
export const PUBLIC_BASE = BACKEND_URL;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearToken();
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export const mediaUrl = (path) => `${BACKEND_URL}${path}`;

/**
 * Fetch a public share / dashboard / portal URL with smart retry.
 *
 * Distinguishes transient errors (cold-start 502/503, network blip,
 * timeout) from genuine "this link is dead" errors (404 / 410 / 422).
 * Public links shown to realtors and buyers used to flash
 * "This dashboard link is invalid" on the FIRST render whenever the
 * production pod was waking up — this helper retries up to N times with
 * a short backoff and only marks the link as truly invalid when the
 * server explicitly says so.
 *
 * Throws either:
 *   { kind: "not_found", status }  — 404 / 410 / 422 (truly invalid)
 *   { kind: "transient", status }  — gave up after retries; show retry UI
 */
export async function fetchPublicWithRetry(url, opts = {}) {
  const {
    retries = 5,
    backoffMs = 1500,
    timeoutMs = 15000,
  } = opts;
  let lastErr = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const r = await axios.get(url, { timeout: timeoutMs });
      return r.data;
    } catch (e) {
      lastErr = e;
      const status = e?.response?.status;
      // Genuinely-dead link — fail fast.
      if (status === 404 || status === 410 || status === 422) {
        throw { kind: "not_found", status };
      }
      // Transient (network, 5xx, timeout, CORS, undefined status) — retry.
      if (attempt < retries) {
        await new Promise((res) => setTimeout(res, backoffMs * (attempt + 1)));
        continue;
      }
    }
  }
  throw {
    kind: "transient",
    status: lastErr?.response?.status,
    error: lastErr,
  };
}
